import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  CssBaseline,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import axios from 'axios';
import './CookiePrivacyPolicyPage.css';

// 'verifying' → 'redirecting' → (navigate) | 'error'
const REDIRECT_DELAY_MS = 2200;

const stepDotStyle = (active) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: active ? '#1976d2' : '#cfd8dc',
  display: 'inline-block',
  margin: '0 5px',
  transition: 'background 0.4s',
});

const VerifyUser = () => {
  const history = useHistory();
  const location = useLocation();

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'redirecting' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [countdown, setCountdown] = useState(Math.round(REDIRECT_DELAY_MS / 1000));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventId = params.get('eventid');
    const findId = params.get('findid');

    if (!eventId || !findId) {
      setErrorMsg('Invalid verification link. Missing eventid or findid.');
      setStatus('error');
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/public/api/registrant/verify`,
          { params: { registrantFindId: findId, eventid: eventId } }
        );

        const data = res.data;

        if (data && data.data && data.data.isVerified) {
          setStatus('redirecting');

          let remaining = Math.round(REDIRECT_DELAY_MS / 1000);
          setCountdown(remaining);

          const ticker = setInterval(() => {
            remaining -= 1;
            setCountdown(remaining);
            if (remaining <= 0) clearInterval(ticker);
          }, 1000);

          setTimeout(() => {
            history.replace(`/broadcast/${eventId}`);
          }, REDIRECT_DELAY_MS);
        } else {
          setErrorMsg('Verification failed. Please try again or contact support.');
          setStatus('error');
        }
      } catch (err) {
        const msg =
          err.response?.data || err.message || 'Failed to verify. Please try again.';
        setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
        setStatus('error');
      }
    };

    verify();
  }, []);

  return (
    <Container maxWidth='sm' className='policy-container'>
      <CssBaseline />
      <Grid container justifyContent='center' style={{ minHeight: '100vh', alignItems: 'center' }}>
        <Grid item xs={12}>
          <Card
            elevation={6}
            style={{
              borderRadius: 16,
              textAlign: 'center',
              padding: '48px 32px',
            }}
          >
            <CardContent>
              {status === 'verifying' && (
                <>
                  <CircularProgress size={56} thickness={4} style={{ marginBottom: 24 }} />
                  <Typography variant='h5' gutterBottom style={{ fontWeight: 600 }}>
                    Verifying your account
                  </Typography>
                  <Typography color='textSecondary'>
                    Please wait while we confirm your registration&hellip;
                  </Typography>
                  <div style={{ marginTop: 28 }}>
                    <span style={stepDotStyle(true)} />
                    <span style={stepDotStyle(false)} />
                  </div>
                </>
              )}

              {status === 'redirecting' && (
                <>
                  <CheckCircleOutlineIcon
                    style={{ fontSize: 64, color: '#43a047', marginBottom: 16 }}
                  />
                  <Typography variant='h5' gutterBottom style={{ fontWeight: 600 }}>
                    You&apos;re verified!
                  </Typography>
                  <Typography color='textSecondary' style={{ marginBottom: 8 }}>
                    Your account has been confirmed successfully.
                  </Typography>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    style={{ marginBottom: 24 }}
                  >
                    Redirecting you to the broadcast in{' '}
                    <strong>{countdown > 0 ? countdown : 1}s</strong>&hellip;
                  </Typography>
                  <CircularProgress
                    size={28}
                    thickness={5}
                    style={{ color: '#43a047' }}
                  />
                  <div style={{ marginTop: 24 }}>
                    <span style={stepDotStyle(true)} />
                    <span style={stepDotStyle(true)} />
                  </div>
                </>
              )}

              {status === 'error' && (
                <>
                  <ErrorOutlineIcon
                    style={{ fontSize: 64, color: '#e53935', marginBottom: 16 }}
                  />
                  <Typography
                    variant='h5'
                    style={{ fontWeight: 600, color: '#e53935', marginBottom: 12 }}
                  >
                    Verification Failed
                  </Typography>
                  <Typography color='textSecondary'>{errorMsg}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default React.memo(VerifyUser);
